import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Drawer as KMuiDrawer } from '@mxenabled/mxui'

import Footer from './Footer'
import Header from './Header'

export type CopyObject = {
  close_drawer_aria: string
}

interface DrawerProps {
  ariaLabelClose?: string
  className?: string
  footerStyles?: SxProps
  hasSecondaryAction?: boolean
  id?: string
  isOpen: boolean
  isPrimaryDisabled?: boolean
  onClose: () => void
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  primaryButtonProps?: React.ComponentProps<typeof Button>
  primaryColor?: 'primary' | 'inherit' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  primaryText?: string
  secondaryColor?: 'primary' | 'inherit' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  secondaryText?: string
  shouldShowHeaderBorders?: boolean
  shouldShowHeaderShadow?: boolean
  sx?: SxProps
  title: string
}

const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  ariaLabelClose = 'Close',
  children,
  footerStyles = {},
  hasSecondaryAction = true,
  id,
  isOpen,
  isPrimaryDisabled,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
  primaryButtonProps,
  primaryColor = 'primary',
  primaryText,
  secondaryColor = 'secondary',
  secondaryText,
  shouldShowHeaderBorders = true,
  shouldShowHeaderShadow = false,
  title,
  ...rest
}) => {
  return (
    <KMuiDrawer
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          'aria-labelledby': `${title?.replace(/\s+/g, '-').toLowerCase()}-header-title`,
        },
      }}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: 'background.default',
          borderRadius: 0,
        },
        height: '100%',
        '& .MuiToolbar-root': {
          backgroundColor: 'background.paper',
        },
      }}
      {...rest}
    >
      <Stack height="100%" justifyContent="space-between">
        <Header
          ariaLabelClose={ariaLabelClose}
          onClose={onClose}
          shouldShowBorders={shouldShowHeaderBorders}
          shouldShowShadow={shouldShowHeaderShadow}
          title={title}
        />

        <Box height="100%" overflow="auto">
          {children}
        </Box>

        {onPrimaryAction && (
          <Footer
            footerStyles={footerStyles}
            hasSecondaryAction={hasSecondaryAction}
            isPrimaryDisabled={isPrimaryDisabled}
            onPrimaryAction={onPrimaryAction}
            onSecondaryAction={onSecondaryAction ?? onClose}
            primaryButtonProps={primaryButtonProps}
            primaryColor={primaryColor}
            primaryText={primaryText}
            secondaryColor={secondaryColor}
            secondaryText={secondaryText}
          />
        )}
      </Stack>
    </KMuiDrawer>
  )
}

export default Drawer
